import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BanknotesIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  CreditCardIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ClockIcon,
  CalendarIcon,
  ChevronDownIcon,
  PlusCircleIcon,
  QrCodeIcon,
  DocumentTextIcon,
  LockClosedIcon,
  CogIcon,
  ShieldCheckIcon,
  ChartBarIcon,
  BanknotesIcon as MoneyIcon
} from '@heroicons/react/24/outline'
import { hubnaiAPI } from '@/services/api'
import type { HubnaiCredentials, HubnaiBalance, HubnaiCharge } from '@/services/api'
import CreateHubnaiChargePixModal from '@/components/payments/CreateHubnaiChargePixModal'
import CreateHubnaiChargeBoletoModal from '@/components/payments/CreateHubnaiChargeBoletoModal'
import CreateHubnaiChargeCardModal from '@/components/payments/CreateHubnaiChargeCardModal'
import CreateHubnaiTransferModal from '@/components/payments/CreateHubnaiTransferModal'

const GatewayFinProDashboard: React.FC = () => {
  const navigate = useNavigate()

  const [credentials, setCredentials] = useState<HubnaiCredentials | null>(null)
  const [balance, setBalance] = useState<HubnaiBalance | null>(null)
  const [charges, setCharges] = useState<HubnaiCharge[]>([])
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [showCreateMenu, setShowCreateMenu] = useState(false)
  const [showPixModal, setShowPixModal] = useState(false)
  const [showBoletoModal, setShowBoletoModal] = useState(false)
  const [showCardModal, setShowCardModal] = useState(false)
  const [showTransferModal, setShowTransferModal] = useState(false)

  // Filtros
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all')
  const [periodFilter, setPeriodFilter] = useState(30)
  const [periodLabel, setPeriodLabel] = useState('Últimos 30 dias')
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false)
  const [customStartDate, setCustomStartDate] = useState('')
  const [customEndDate, setCustomEndDate] = useState('')
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false)

  // Paginação
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 20

  useEffect(() => {
    loadDashboardData()
  }, [statusFilter, paymentMethodFilter, periodFilter])

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('.period-filter-dropdown') && !target.closest('.create-menu-dropdown')) {
        setShowPeriodDropdown(false)
        setShowCustomDatePicker(false)
        setShowCreateMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)

      // Verificar se há credenciais configuradas
      const credsData = await hubnaiAPI.getCredentials()
      setCredentials(credsData)

      if (!credsData || !credsData.active) {
        // Não há credenciais configuradas
        setLoading(false)
        return
      }

      // Buscar saldo
      const balanceData = await hubnaiAPI.getBalance()
      setBalance(balanceData)

      // Buscar cobranças com filtros
      const startDate = new Date()
      startDate.setDate(startDate.getDate() - periodFilter)

      const params: any = {
        limit: 1000, // Buscar todas para filtrar localmente
        skip: 0
      }

      if (statusFilter !== 'all') {
        params.status = statusFilter
      }

      if (paymentMethodFilter !== 'all') {
        params.payment_method = paymentMethodFilter
      }

      if (periodFilter > 0) {
        params.start_date = startDate.toISOString().split('T')[0]
        params.end_date = new Date().toISOString().split('T')[0]
      }

      const chargesData = await hubnaiAPI.listCharges(params)
      setCharges(chargesData)

    } catch (error: any) {
      console.error('Erro ao carregar dados do Gateway FinPro:', error)
      // Se erro 404, significa que não há credenciais configuradas
      if (error.response?.status === 404) {
        setCredentials(null)
      } else {
        alert('Erro ao carregar dados: ' + (error.response?.data?.detail || error.message))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSync = async () => {
    try {
      setSyncing(true)

      const startDate = new Date()
      startDate.setDate(startDate.getDate() - periodFilter)

      const params = {
        start_date: startDate.toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0]
      }

      await hubnaiAPI.syncData(params)
      await loadDashboardData()
      alert('Sincronização concluída com sucesso!')
    } catch (error: any) {
      console.error('Erro ao sincronizar:', error)
      alert('Erro ao sincronizar: ' + (error.response?.data?.detail || error.message))
    } finally {
      setSyncing(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value / 100) // Valores em centavos
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; class: string }> = {
      pending: { label: 'Pendente', class: 'bg-yellow-100 text-yellow-800' },
      paid: { label: 'Pago', class: 'bg-green-100 text-green-800' },
      failed: { label: 'Falhou', class: 'bg-red-100 text-red-800' },
      cancelled: { label: 'Cancelado', class: 'bg-gray-100 text-gray-800' },
      expired: { label: 'Expirado', class: 'bg-orange-100 text-orange-800' }
    }

    const statusInfo = statusMap[status] || { label: status, class: 'bg-gray-100 text-gray-800' }

    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusInfo.class}`}>
        {statusInfo.label}
      </span>
    )
  }

  const getPaymentMethodBadge = (method: string) => {
    const methodMap: Record<string, { label: string; icon: any; color: string }> = {
      pix: { label: 'PIX', icon: QrCodeIcon, color: 'text-purple-600' },
      boleto: { label: 'Boleto', icon: DocumentTextIcon, color: 'text-orange-600' },
      credit_card: { label: 'Crédito', icon: CreditCardIcon, color: 'text-blue-600' },
      debit_card: { label: 'Débito', icon: CreditCardIcon, color: 'text-green-600' }
    }

    const methodInfo = methodMap[method] || { label: method, icon: CreditCardIcon, color: 'text-gray-600' }
    const Icon = methodInfo.icon

    return (
      <div className="flex items-center gap-2">
        <Icon className={`w-5 h-5 ${methodInfo.color}`} />
        <span className="text-sm text-gray-700">{methodInfo.label}</span>
      </div>
    )
  }

  const handlePeriodChange = (days: number, label: string) => {
    setPeriodFilter(days)
    setPeriodLabel(label)
    setShowPeriodDropdown(false)
    setShowCustomDatePicker(false)
    setCurrentPage(1)
  }

  const handleCustomPeriod = () => {
    if (!customStartDate || !customEndDate) {
      alert('Por favor, selecione as datas de início e fim')
      return
    }

    const start = new Date(customStartDate)
    const end = new Date(customEndDate)
    const diffTime = Math.abs(end.getTime() - start.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    setPeriodFilter(diffDays)
    setPeriodLabel(`${start.toLocaleDateString('pt-BR')} - ${end.toLocaleDateString('pt-BR')}`)
    setShowPeriodDropdown(false)
    setShowCustomDatePicker(false)
    setCurrentPage(1)
  }

  const getDaysFromStartOfMonth = () => {
    const now = new Date()
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    const diffTime = Math.abs(now.getTime() - startOfMonth.getTime())
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1
  }

  const getDaysFromLastMonth = () => {
    const now = new Date()
    const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const diffTime = Math.abs(now.getTime() - startOfLastMonth.getTime())
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  }

  // Cálculo de paginação
  const totalPages = Math.ceil(charges.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedCharges = charges.slice(startIndex, endIndex)

  // Calcular estatísticas
  const paidCharges = charges.filter(c => c.status === 'paid')
  const totalCharges = charges.length

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  // Tela de "não configurado"
  if (!credentials || !credentials.active) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-8 text-white shadow-lg">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-3 bg-white/20 rounded-xl">
              <ShieldCheckIcon className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Gateway de Pagamentos</h1>
              <p className="text-blue-100">Sistema nativo de cobranças e transferências</p>
            </div>
          </div>
        </div>

        {/* Not Configured Message */}
        <div className="bg-white rounded-xl border-2 border-dashed border-gray-300 p-12 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <LockClosedIcon className="w-8 h-8 text-gray-400" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Gateway não configurado</h2>
            <p className="text-gray-600 mb-6">
              O Gateway de Pagamentos ainda não foi configurado para sua conta. Entre em contato com o suporte para ativar este recurso.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Benefícios do Gateway FinPro:</strong>
              </p>
              <ul className="text-sm text-blue-700 mt-2 space-y-1 text-left">
                <li>• Cobranças via PIX, Boleto e Cartão</li>
                <li>• Sincronização automática com financeiro</li>
                <li>• Transferências e saques integrados</li>
                <li>• Gestão completa de recebíveis</li>
              </ul>
            </div>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => navigate('/payments/gateway-finpro/settings')}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg font-medium"
              >
                Configurar Gateway
              </button>
              <button
                onClick={() => navigate('/financeiro')}
                className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Voltar para Financeiro
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl">
                <ShieldCheckIcon className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gateway de Pagamentos</h1>
                <p className="text-sm text-gray-500">Sistema Nativo FinPro • {credentials.sandbox ? 'Sandbox' : 'Produção'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-2 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-sm font-medium">
                <CheckCircleIcon className="w-4 h-4" />
                Ativo
              </span>
              <button
                onClick={() => navigate('/payments/gateway-finpro/settings')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                title="Configurações"
              >
                <CogIcon className="w-5 h-5 text-gray-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Action Bar */}
        <div className="px-6 py-4 bg-gray-50">
          <div className="flex items-center justify-between gap-4">
            {/* Criar Cobrança - Dropdown */}
            <div className="relative create-menu-dropdown">
              <button
                onClick={() => setShowCreateMenu(!showCreateMenu)}
                className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg font-medium"
              >
                <PlusCircleIcon className="w-5 h-5" />
                Nova Cobrança
                <ChevronDownIcon className="w-4 h-4" />
              </button>

              {showCreateMenu && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowCreateMenu(false)}
                  />
                  <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-20">
                    <button
                      onClick={() => {
                        setShowPixModal(true)
                        setShowCreateMenu(false)
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-purple-50 transition-colors text-left"
                    >
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <QrCodeIcon className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">PIX</p>
                        <p className="text-xs text-gray-500">Pagamento instantâneo</p>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setShowBoletoModal(true)
                        setShowCreateMenu(false)
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-orange-50 transition-colors text-left"
                    >
                      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                        <DocumentTextIcon className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Boleto</p>
                        <p className="text-xs text-gray-500">Pagamento bancário</p>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setShowCardModal(true)
                        setShowCreateMenu(false)
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-blue-50 transition-colors text-left"
                    >
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                        <CreditCardIcon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Cartão</p>
                        <p className="text-xs text-gray-500">Crédito ou Débito</p>
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Botões de Ação */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/payments/gateway-finpro/transfers')}
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all shadow-sm font-medium"
              >
                <ArrowUpIcon className="w-5 h-5" />
                Ver Transferências
              </button>
              <button
                onClick={handleSync}
                disabled={syncing}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg font-medium"
              >
                <ArrowPathIcon className={`w-5 h-5 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Sincronizando...' : 'Sincronizar'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modais */}
      <CreateHubnaiChargePixModal
        isOpen={showPixModal}
        onClose={() => setShowPixModal(false)}
        onSuccess={() => loadDashboardData()}
      />
      <CreateHubnaiChargeBoletoModal
        isOpen={showBoletoModal}
        onClose={() => setShowBoletoModal(false)}
        onSuccess={() => loadDashboardData()}
      />
      <CreateHubnaiChargeCardModal
        isOpen={showCardModal}
        onClose={() => setShowCardModal(false)}
        onSuccess={() => loadDashboardData()}
      />
      <CreateHubnaiTransferModal
        isOpen={showTransferModal}
        onClose={() => setShowTransferModal(false)}
        onSuccess={() => {
          setShowTransferModal(false)
          loadDashboardData()
        }}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Disponível</span>
            <BanknotesIcon className="w-5 h-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {balance ? formatCurrency(balance.available) : 'R$ 0,00'}
          </p>
          <p className="text-xs text-gray-500 mt-1 mb-3">Saldo disponível</p>
          <button
            onClick={() => setShowTransferModal(true)}
            disabled={!balance || balance.available <= 0}
            className="w-full px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium transition-colors shadow-sm hover:shadow-md flex items-center justify-center gap-2"
          >
            <ArrowUpIcon className="w-4 h-4" />
            Solicitar Saque
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Pendente</span>
            <ClockIcon className="w-5 h-5 text-orange-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {balance ? formatCurrency(balance.pending) : 'R$ 0,00'}
          </p>
          <p className="text-xs text-gray-500 mt-1">A receber</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Bloqueado</span>
            <ExclamationCircleIcon className="w-5 h-5 text-red-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {balance ? formatCurrency(balance.blocked) : 'R$ 0,00'}
          </p>
          <p className="text-xs text-gray-500 mt-1">Em análise</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Total</span>
            <MoneyIcon className="w-5 h-5 text-blue-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {balance ? formatCurrency(balance.total) : 'R$ 0,00'}
          </p>
          <p className="text-xs text-gray-500 mt-1">Saldo total</p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-600">Cobranças</span>
            <ChartBarIcon className="w-5 h-5 text-purple-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {totalCharges}
          </p>
          <p className="text-xs text-gray-500 mt-1">{paidCharges.length} pagas</p>
        </div>
      </div>

      {/* Charges List */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Cobranças</h2>

            {/* Filtro de Período - Dropdown */}
            <div className="relative period-filter-dropdown">
              <button
                onClick={() => setShowPeriodDropdown(!showPeriodDropdown)}
                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
              >
                <CalendarIcon className="w-5 h-5 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">{periodLabel}</span>
                <ChevronDownIcon className="w-4 h-4 text-gray-500" />
              </button>

              {/* Dropdown Menu */}
              {showPeriodDropdown && (
                <div className="absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-lg shadow-xl z-50">
                  <div className="py-1">
                    <button
                      onClick={() => handlePeriodChange(30, 'Últimos 30 dias')}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-between"
                    >
                      <span>Últimos 30 dias</span>
                      {periodFilter === 30 && periodLabel === 'Últimos 30 dias' && (
                        <CheckCircleIcon className="w-4 h-4 text-blue-600" />
                      )}
                    </button>
                    <button
                      onClick={() => handlePeriodChange(60, 'Últimos 60 dias')}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-between"
                    >
                      <span>Últimos 60 dias</span>
                      {periodFilter === 60 && periodLabel === 'Últimos 60 dias' && (
                        <CheckCircleIcon className="w-4 h-4 text-blue-600" />
                      )}
                    </button>
                    <button
                      onClick={() => handlePeriodChange(getDaysFromStartOfMonth(), 'Mês atual')}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-between"
                    >
                      <span>Mês atual</span>
                      {periodLabel === 'Mês atual' && (
                        <CheckCircleIcon className="w-4 h-4 text-blue-600" />
                      )}
                    </button>
                    <button
                      onClick={() => handlePeriodChange(getDaysFromLastMonth(), 'Mês passado')}
                      className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-between"
                    >
                      <span>Mês passado</span>
                      {periodLabel === 'Mês passado' && (
                        <CheckCircleIcon className="w-4 h-4 text-blue-600" />
                      )}
                    </button>

                    <div className="border-t border-gray-200 my-1"></div>

                    <button
                      onClick={() => {
                        setShowCustomDatePicker(!showCustomDatePicker)
                        setShowPeriodDropdown(false)
                      }}
                      className="w-full px-4 py-2.5 text-left text-sm font-medium text-blue-600 hover:bg-blue-50 transition-colors"
                    >
                      Selecionar período
                    </button>
                  </div>
                </div>
              )}

              {/* Date Picker Personalizado */}
              {showCustomDatePicker && (
                <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-xl z-50 p-4">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Período Personalizado</h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Data Início</label>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Data Fim</label>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={handleCustomPeriod}
                        className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                      >
                        Aplicar
                      </button>
                      <button
                        onClick={() => setShowCustomDatePicker(false)}
                        className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium transition-colors"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Filtros de Status e Método */}
          <div className="flex gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Todos</option>
                <option value="pending">Pendente</option>
                <option value="paid">Pago</option>
                <option value="failed">Falhou</option>
                <option value="cancelled">Cancelado</option>
                <option value="expired">Expirado</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Método</label>
              <select
                value={paymentMethodFilter}
                onChange={(e) => setPaymentMethodFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">Todos</option>
                <option value="pix">PIX</option>
                <option value="boleto">Boleto</option>
                <option value="credit_card">Cartão de Crédito</option>
                <option value="debit_card">Cartão de Débito</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          {charges.length === 0 ? (
            <div className="p-12 text-center">
              <ClockIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 font-medium">Lista de cobranças não disponível</p>
              <p className="text-sm text-gray-400 mt-2">
                A API Hubnai não fornece endpoint de listagem de cobranças.
              </p>
              <div className="mt-4 max-w-md mx-auto bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>💡 Nota técnica:</strong> As cobranças criadas são processadas com sucesso,
                  mas não aparecem nesta lista. Para visualizar cobranças, você pode:
                </p>
                <ul className="text-xs text-blue-700 mt-2 text-left space-y-1">
                  <li>• Salvar o QR Code e payment ID após criar cada cobrança</li>
                  <li>• Monitorar webhooks de confirmação de pagamento</li>
                  <li>• Consultar liquidações através do menu "Ver Transferências"</li>
                </ul>
              </div>
              <p className="text-xs text-gray-400 mt-4">
                Crie cobranças PIX ou Cartão usando o botão "Nova Cobrança" acima
              </p>
            </div>
          ) : (
            <>
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cliente</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Método</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Valor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedCharges.map((charge) => (
                    <tr
                      key={charge.id}
                      onClick={() => navigate(`/payments/gateway-finpro/charges/${charge.id}`)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(charge.created_at)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <div className="font-medium">{charge.customer_name || 'Cliente avulso'}</div>
                        {charge.customer_email && (
                          <div className="text-xs text-gray-500">{charge.customer_email}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getPaymentMethodBadge(charge.payment_method)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(charge.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-right text-gray-900">
                        {formatCurrency(charge.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Exibindo <span className="font-medium">{startIndex + 1}</span> a{' '}
                    <span className="font-medium">{Math.min(endIndex, charges.length)}</span> de{' '}
                    <span className="font-medium">{charges.length}</span> cobranças
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Anterior
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                        const showPage =
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)

                        if (!showPage) {
                          if (page === 2 && currentPage > 3) {
                            return <span key={page} className="px-2 text-gray-500">...</span>
                          }
                          if (page === totalPages - 1 && currentPage < totalPages - 2) {
                            return <span key={page} className="px-2 text-gray-500">...</span>
                          }
                          return null
                        }

                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                              currentPage === page
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        )
                      })}
                    </div>

                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Próximo
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Last Update Info */}
      {balance && balance.updated_at && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-sm text-blue-800">
            <CheckCircleIcon className="w-5 h-5" />
            <span>Última atualização: {formatDate(balance.updated_at)}</span>
          </div>
        </div>
      )}
    </div>
  )
}

export default GatewayFinProDashboard
